import React, { useEffect, useRef, useState } from 'react';
import { personalInfo, networkTags } from '../data/mock';
import { Mail, MapPin, Linkedin, Github, Youtube, Globe, ArrowUpRight, Shield, Send, Instagram, Facebook, MessageSquare } from 'lucide-react';
import { Badge } from './ui/badge';

function useInView(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return visible;
}

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const socialLinksArr = [
  { icon: Linkedin, href: personalInfo.socialLinks.linkedin, label: 'LinkedIn' },
  { icon: Github, href: personalInfo.socialLinks.github, label: 'GitHub' },
  { icon: XIcon, href: personalInfo.socialLinks.x, label: 'X' },
  { icon: Youtube, href: personalInfo.socialLinks.youtube, label: 'YouTube' },
  { icon: Instagram, href: personalInfo.socialLinks.instagram, label: 'Instagram' },
  { icon: Facebook, href: personalInfo.socialLinks.facebook, label: 'Facebook' },
  { icon: MessageSquare, href: personalInfo.socialLinks.telegram, label: 'Telegram' },
  { icon: MessageSquare, href: personalInfo.socialLinks.discord, label: 'Discord' },
  { icon: Globe, href: personalInfo.socialLinks.cyberWithVishal, label: 'CyberWithVishal' },
  { icon: Globe, href: personalInfo.socialLinks.saraswatiRepo, label: 'Saraswati Repo' },
];

export default function ContactSection() {
  const sectionRef = useRef(null);
  const visible = useInView(sectionRef);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - saves to localStorage
    const messages = JSON.parse(localStorage.getItem('contact_messages') || '[]');
    messages.push({ ...formData, timestamp: new Date().toISOString() });
    localStorage.setItem('contact_messages', JSON.stringify(messages));
    setSent(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <>
      <section id="contact" className="relative" ref={sectionRef}>
        <div className="section-container">
          <div className={`fade-in-up ${visible ? 'visible' : ''}`}>
            <p className="section-label">// Get In Touch</p>
            <h2 className="section-title">Let's Connect</h2>
            <div className="glow-line w-20 mb-10" />
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            {/* Left - Info */}
            <div className={`space-y-6 fade-in-up ${visible ? 'visible' : ''}`} style={{ transitionDelay: '0.1s' }}>
              <p className="text-[#94a3b8] leading-relaxed text-[15px]">
                Interested in collaborating on cybersecurity initiatives, speaking opportunities, or enterprise security consulting? I'd love to hear from you.
              </p>

              {/* Contact details */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#00e5ff]/8 border border-[#00e5ff]/20 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#00e5ff]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b] font-['JetBrains_Mono']">Email</p>
                    <p className="text-sm text-[#c8d1dc]">{personalInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#00e5ff]/8 border border-[#00e5ff]/20 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-[#00e5ff]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b] font-['JetBrains_Mono']">Location</p>
                    <p className="text-sm text-[#c8d1dc]">{personalInfo.location}</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="pt-4">
                <p className="text-xs text-[#64748b] font-['JetBrains_Mono'] tracking-wider uppercase mb-4">Connect</p>
                <div className="flex flex-wrap gap-3">
                  {socialLinksArr.map(({ icon: Icon, href, label }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#1e293b] text-[#94a3b8] hover:border-[#00e5ff]/40 hover:text-[#00e5ff] hover:bg-[#00e5ff]/5 transition-colors duration-300 text-sm"
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-xs">{label}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Network tags */}
              <div className="pt-4">
                <p className="text-xs text-[#64748b] font-['JetBrains_Mono'] tracking-wider uppercase mb-3">Network</p>
                <div className="flex flex-wrap gap-2">
                  {networkTags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="border-[#1e293b] text-[#64748b] text-[10px] bg-transparent"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className={`fade-in-up ${visible ? 'visible' : ''}`} style={{ transitionDelay: '0.2s' }}>
              <form onSubmit={handleSubmit} className="cyber-card p-6 space-y-5">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse" />
                  <span className="text-xs text-[#64748b] font-['JetBrains_Mono']">secure_message.init()</span>
                </div>

                <div>
                  <label className="block text-xs text-[#64748b] font-['JetBrains_Mono'] mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-[#0c1017] border border-[#1e293b] rounded-md px-4 py-3 text-sm text-[#f1f5f9] placeholder-[#4a5568] focus:outline-none focus:border-[#00e5ff]/50 focus:ring-1 focus:ring-[#00e5ff]/20 transition-colors duration-200"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#64748b] font-['JetBrains_Mono'] mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-[#0c1017] border border-[#1e293b] rounded-md px-4 py-3 text-sm text-[#f1f5f9] placeholder-[#4a5568] focus:outline-none focus:border-[#00e5ff]/50 focus:ring-1 focus:ring-[#00e5ff]/20 transition-colors duration-200"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#64748b] font-['JetBrains_Mono'] mb-2">Subject</label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full bg-[#0c1017] border border-[#1e293b] rounded-md px-4 py-3 text-sm text-[#f1f5f9] placeholder-[#4a5568] focus:outline-none focus:border-[#00e5ff]/50 focus:ring-1 focus:ring-[#00e5ff]/20 transition-colors duration-200"
                    placeholder="Subject"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#64748b] font-['JetBrains_Mono'] mb-2">Message</label>
                  <textarea
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full bg-[#0c1017] border border-[#1e293b] rounded-md px-4 py-3 text-sm text-[#f1f5f9] placeholder-[#4a5568] focus:outline-none focus:border-[#00e5ff]/50 focus:ring-1 focus:ring-[#00e5ff]/20 transition-colors duration-200 resize-none"
                    placeholder="Your message..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full btn-cyber-filled rounded-md flex items-center justify-center gap-2 py-3"
                >
                  <Send className="w-4 h-4" /> Send Message
                </button>

                {sent && (
                  <div className="text-center text-sm text-[#00ff88] font-['JetBrains_Mono'] animate-pulse">
                    Message sent successfully!
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1e293b]/60 bg-[#06080d]">
        <div className="max-w-[1200px] mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-[#00e5ff]/10 border border-[#00e5ff]/30 flex items-center justify-center">
                <Shield className="w-3.5 h-3.5 text-[#00e5ff]" />
              </div>
              <span className="font-['JetBrains_Mono'] text-sm font-semibold text-[#f1f5f9]">
                vishal<span className="text-[#00e5ff]">.sec</span>
              </span>
            </div>

            {/* Center */}
            <p className="text-xs text-[#64748b] font-['JetBrains_Mono']">
              &copy; {new Date().getFullYear()} Vishal Majithia. Securing the Digital Frontier with Zero‑Trust AI.
            </p>

            {/* Social */}
            <div className="flex items-center gap-3">
              {socialLinksArr.slice(0, 6).map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-8 h-8 rounded-md border border-[#1e293b] flex items-center justify-center text-[#64748b] hover:text-[#00e5ff] hover:border-[#00e5ff]/30 transition-colors duration-300"
                >
                  <Icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
